import type { BIIncludeExcludeListItemProps } from '@pulsepointinc/bi-components';
import type { HierarchyItem } from '../../../../../../lib/api/hierarchies/dto';
import type { TaxonomyID, TreeNode } from './types';

export const getQueryParams = (taxonomyID: string, prefix: string) => {
  const levels = taxonomyID.split(/[-.]/).filter(Boolean);

  return {
    dictionary: prefix,
    minDepth: levels.length > 1 ? levels.length - 1 : levels.length,
    maxDepth: levels.length,
    taxonomies: taxonomyID ? [taxonomyID] : undefined,
  };
};

export const getCascadingTaxonomyIds = (taxonomyId: string) => {
  const levels = taxonomyId.split(/(?=[-.])/).filter(Boolean);
  return levels.map((_, index) => levels.slice(0, levels.length - index).join(''));
};

export const findNodeAndParent = (taxonomyId: string, currentTree: Record<string, TreeNode>) => {
  const taxonomySplitPath = taxonomyId.split(/[-.]/);
  const key = taxonomySplitPath.pop();

  const ancestors: TreeNode[] = [];

  const parentNode = taxonomySplitPath.reduce<TreeNode['children'] | null>((tree, key) => {
    const parent = tree?.[key];

    if (parent) {
      ancestors.push(parent);
    }

    return parent?.children ?? null;
  }, currentTree);

  return {
    parentNode,
    ancestors,
    key,
    node: parentNode && key ? parentNode[key] : null,
  };
};

export const mapCategoriesTree = (
  hierarchyItems: HierarchyItem[],
  currentTree: Record<TaxonomyID, TreeNode>,
  whenNodeExists: (node: TreeNode, category: HierarchyItem) => TreeNode,
  whenNodeIsMissing: (category: HierarchyItem) => TreeNode,
) => {
  return hierarchyItems.reduce(
    (tree, category) => {
      const { parentNode, key } = findNodeAndParent(category.taxonomy_id, tree);

      if (parentNode && key) {
        const existing = parentNode[key];

        if (!existing) {
          parentNode[key] = whenNodeIsMissing(category);
        } else {
          const node: TreeNode = {
            ...existing,
            has_children: category.has_children ?? existing.has_children,
          };
          parentNode[key] = whenNodeExists(node, category);
        }
      }

      return tree;
    },
    { ...currentTree },
  );
};

// FIXME: Remove this mapping after aligning to one version everywhere
export const stateMap: Record<NonNullable<TreeNode['state']>, BIIncludeExcludeListItemProps['state']> = {
  allow: 'check',
  block: 'block',
  'semi-allow': 'semi-check',
  'semi-block': 'semi-block',
};

export type Alias = string;
export const dictionaryAliasMappers: Record<'MeSH' | 'IAB', (alias: Alias) => string> = {
  MeSH: (alias: Alias): string => {
    return `${alias.replace(/(\w)(\d)/, '$1.$2')}`;
  },
  IAB: (alias: Alias): string => {
    const leadingZeroFormatter = Intl.NumberFormat('en-US', {
      minimumIntegerDigits: 2,
    });

    return `${alias
      .replace(/IAB/gi, '')
      .split('-')
      .filter(Boolean)
      .map((part) => leadingZeroFormatter.format(Number(part)))
      .join('-')}`;
  },
};

export const toTitleCase = (str: string): string => {
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const isExpandableTreeNode = (item: TreeNode): boolean => {
  return !!item.isLoading || Object.values(item.children).length > 0 || !!item.has_children;
};
